import React from 'react'

import {noop} from 'es-toolkit'
import {isElement} from 'react-is'

import useUniqueId from '../../hooks/use-unique-id'
import cn from '../../libs/class-names'
import {mapChildrenWithFragments} from '../../services/react-children'

import {CommonProps, Overwrite} from '../../types'

import RadioPillItem from './radio-pill-item'

type Props = Overwrite<
  CommonProps<'div'>,
  {
    children: React.ReactNode
    name?: string
    value?: string
    multiColumnToolLayoutMobile?: boolean
    multiColumn?: boolean
    onChange?: (
      value: string,
      name: string | undefined,
      event: React.ChangeEvent<HTMLInputElement>
    ) => void
  }
>

type ItemProps = React.ComponentProps<typeof RadioPillItem>

const RadioPillField = ({
  className,
  children,
  value,
  name,
  multiColumnToolLayoutMobile = false,
  multiColumn = false,
  onChange = noop,
  ...props
}: Props) => {
  const actualName = useUniqueId(name)

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onChange(event.target.value, actualName, event)
  }

  const childElements = mapChildrenWithFragments<ItemProps>(children).filter(
    (child): child is React.ReactElement<ItemProps> => isElement(child)
  )
  const firstColumn = childElements.slice(0, Math.ceil(childElements.length / 2))
  const secondColumn = childElements.slice(Math.ceil(childElements.length / 2))

  const renderItem = (item: React.ReactElement<ItemProps>, key: number) =>
    React.cloneElement(item, {
      key,
      onChange: handleChange,
      name: actualName,
      checked: value === item.props.value,
    })

  return (
    <div
      {...props}
      className={cn('RadioPillField', {multiColumnToolLayoutMobile, multiColumn}, className)}
    >
      <div className="RadioPillField__column">{firstColumn.map(renderItem)}</div>
      <div className="RadioPillField__column">{secondColumn.map(renderItem)}</div>
    </div>
  )
}

export default RadioPillField
